<template>
  <v-container>
    <v-container>
      <v-card>
        <v-card-title>
          {{ $t("navSessions") }}
        </v-card-title>
        <v-card-text>
          <v-container class="py-5 px-3">
            <v-row>
              <v-col cols="12" sm="2" class="py-0">
                <v-menu
                  v-model="datepickerFrom"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="
                        moment(selectedFilters.date_from).format('DD.MM.YYYY')
                      "
                      :label="$t('paymentsFilterStart')"
                      type="text"
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-icon="mdi-calendar-start"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :max="moment.parseZone().format('YYYY-MM-DD')"
                    v-model="selectedFilters.date_from"
                    no-title
                    scrollable
                    :locale="$i18n.locale"
                    @change="uploadSessions()"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="2" class="py-0">
                <v-menu
                  v-model="datepickerTo"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="
                        moment(selectedFilters.date_to).format('DD.MM.YYYY')
                      "
                      :label="$t('paymentsFilterEnd')"
                      type="text"
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-icon="mdi-calendar-end"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    :min="
                      moment
                        .parseZone(selectedFilters.date_from)
                        .format('YYYY-MM-DD')
                    "
                    :max="moment.parseZone().format('YYYY-MM-DD')"
                    v-model="selectedFilters.date_to"
                    no-title
                    scrollable
                    :locale="$i18n.locale"
                    @change="uploadSessions()"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col class="py-0 col-auto ml-auto">
                <v-btn
                  @click="uploadSessions((csv = true))"
                  outlined
                  color="green darken-1"
                  text
                  tile
                  :loading="loadingCSV"
                  :disabled="sessions == null || sessions == [] || loadingCSV"
                  ><v-icon left>mdi-file-export</v-icon>CSV</v-btn
                >
              </v-col>
            </v-row>
          </v-container>
          <v-data-table
            v-if="sessions"
            :headers="headers"
            :items="sessions"
            :items-per-page="20"
            class="elevation-1"
            :no-data-text="$t('sessionsNotFound')"
            :footer-props="{
              'items-per-page-options': limits,
              'items-per-page-text': $t('paymentsRowsPerPage'),
            }"
            :server-items-length="totalPages"
            :page.sync="currentPage"
            :items-per-page.sync="selectedFilters.limit"
            @update:page="onPageUpdate"
            @update:items-per-page="onItemsPerPageUpdate"
          >
            <template v-slot:item.orders_ids="{ item }">
              <span v-for="(id, index) in item.orders_ids" :key="id">
                <router-link
                  :to="{ name: 'order', params: { order_id: id } }"
                  target="_blank"
                  style="text-decoration: none"
                  >{{ id }}</router-link
                >
                <span v-if="index < item.orders_ids.length - 1">, </span>
              </span>
            </template>
            <template v-slot:item.started="{ item }">
              <span>{{ item.started | dateAndTimeFilter }}</span>
            </template>
            <template v-slot:item.ended="{ item }">
              <span>{{ item.ended | dateAndTimeFilter }}</span>
            </template>
            <template v-slot:item.playground="{ item }">
              <span>{{ findPlaygroundNameById(item.playground) }}</span>
            </template>
            <template v-slot:item.game="{ item }">
              <span>{{ findGameNameById(item.game) }}</span>
            </template>
            <template v-slot:item.session_type="{ item }">
              <span>{{ findSessionTypeNameById(item.session_type) }}</span>
            </template>
          </v-data-table>
          <v-skeleton-loader
            v-else
            class="mx-n3 mt-n4"
            type="table-thead, table-tbody, table-tfoot"
            tile
          ></v-skeleton-loader>
        </v-card-text>
      </v-card>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment/moment";
import store from "@/store";

export default {
  name: "LocationSessions",

  beforeRouteEnter: async (to, from, next) => {
    await store.dispatch("loadLocation", to.params.location_id);
    next();
  },

  data() {
    return {
      sessions: [],
      headers: [
        { text: "ID", value: "uuid", width: "10%", sortable: false },
        { text: this.$i18n.t("ordersID"), value: "orders_ids", width: "7%" },
        {
          text: this.$i18n.t("sessionStarted"),
          value: "started",
          width: "10%",
          sortable: false,
        },
        {
          text: this.$i18n.t("sessionEnded"),
          value: "ended",
          width: "10%",
          sortable: false,
        },
        {
          text: this.$i18n.t("sessionSession"),
          value: "session",
          width: "10%",
          sortable: false,
        },
        {
          text: this.$i18n.t("sessionPlayground"),
          value: "playground",
          width: "10%",
          sortable: false,
        },
        {
          text: this.$i18n.t("sessionGame"),
          value: "game",
          width: "10%",
          sortable: false,
        },
        {
          text: this.$i18n.t("sessionPlayersCnt"),
          value: "players_cnt",
          width: "10%",
          sortable: false,
        },
        {
          text: this.$i18n.t("sessionSessionType"),
          value: "session_type",
          width: "10%",
          sortable: false,
        },
      ],
      sessionTypes: [
        {
          id: 1,
          title: this.$i18n.t("sessionCommercialSession"),
        },
        {
          id: 2,
          title: this.$i18n.t("sessionTechnicalTest"),
        },
        {
          id: 3,
          title: this.$i18n.t("sessionFreeShowcase"),
        },
        {
          id: 4,
          title: this.$i18n.t("sessionBreak"),
        },
      ],
      loadingCSV: false,
      sessionsCSV: null,
      datepickerFrom: false,
      datepickerTo: false,
      selectedFilters: {
        date_from: moment.parseZone().startOf("month").format("YYYY-MM-DD"),
        date_to: moment.parseZone().format("YYYY-MM-DD"),
        offset: 0,
        limit: 10,
      },
      totalPages: null,
      limits: [10, 15, 30, 50, 100],
      currentPage: 1,
    };
  },
  mounted() {
    this.uploadSessions();
  },
  computed: {
    ...mapGetters(["location", "locationSessions"]),
  },
  filters: {
    dateAndTimeFilter(value) {
      if (value) {
        return moment.parseZone(value).format("DD.MM.YYYY HH:mm");
      }
    },
  },
  methods: {
    ...mapActions(["loadLocationSessions", "getSessionsCSV"]),
    async sendLoadLocationSessionsRequest(payload) {
      this.sessions = null;
      try {
        await this.loadLocationSessions(payload);
        if (this.locationSessions.results) {
          const response = this.locationSessions;
          this.sessions = response.results;
          this.totalPages = response.count;
        }
      } catch (error) {
        console.error(error);
      }
    },
    uploadSessions(csv) {
      this.selectedFilters.offset =
        this.selectedFilters.limit * (this.currentPage - 1);
      const filteredFilters = Object.entries(this.selectedFilters).reduce(
        (a, [k, v]) =>
          typeof v === "object" && !v.length ? a : ((a[k] = v), a),
        {}
      );
      const payload = Object.keys(filteredFilters)
        .map((key) => key + "=" + filteredFilters[key])
        .join("&");

      if (csv) {
        this.loadingCSV = true;
        this.getSessionsCSV(payload).then((response) => {
          if (response.data) {
            this.sessionsCSV = response.data;
            this.loadingCSV = false;
            this.downloadCSV();
          } else {
            this.sessionsCSV = null;
          }
        });
      } else {
        this.sendLoadLocationSessionsRequest(payload);
      }
    },
    downloadCSV() {
      const anchor = document.createElement("a");
      anchor.href =
        "data:text/csv;charset=utf-8," + encodeURIComponent(this.sessionsCSV);
      anchor.target = "_blank";
      anchor.download = "sessions.csv";
      anchor.click();
    },
    findPlaygroundNameById(playgroundId) {
      const playground = this.location.playgrounds.find(
        (p) => p.id === playgroundId
      );
      return playground ? playground.name : `ID: ${playgroundId}`;
    },
    findGameNameById(gameId) {
      const game = this.location.available_games.find((g) => g.id === gameId);
      return game ? game.name : `ID: ${gameId}`;
    },
    findSessionTypeNameById(typeId) {
      const type = this.sessionTypes.find((t) => t.id === typeId);
      return type ? type.title : `ID: ${typeId}`;
    },
    onPageUpdate(newPage) {
      this.currentPage = newPage;
      this.uploadSessions();
    },
    onItemsPerPageUpdate(newLimit) {
      this.selectedFilters.limit = newLimit;
      this.uploadSessions();
    },
  },
};
</script>
